import { Button, Money, Text } from '@belong/ui';
import {
  MonthlyStatementDetailInfoModel,
  BillingInfoModel,
  GroupedBalancesInfoModel,
} from 'api/models.v2';
import AccountPageSection from 'pages/Accounts/Pages/AccountPage/AccountPageSection/AccountPageSection';
import { getMonthName } from 'pages/Accounts/utils/get-month-name';
import { EARNINGS_STRINGS } from '../Pages/YourPortfolio/Earnings/earnings.strings';
import { MonthSummary } from './month-summary/month-summary';

type UpcomingSectionProps = {
  // Nullable because `Earnings` reads it straight off the store, where it is
  // `null` until the fetch resolves. The body already reads it through `?.`.
  balances: MonthlyStatementDetailInfoModel[] | null;
  selectedHome?: string;
  isResident?: boolean;
  handleFinancingModal?: (
    id: string | number,
    balance: GroupedBalancesInfoModel,
    home: string,
  ) => void;
  handleShowFinanceSuccessModal?: () => void;
  handleShowBalanceDetailsModal?: (
    balance: GroupedBalancesInfoModel,
    home: string,
    billingInfo?: BillingInfoModel,
  ) => void;
  address?: string;
  hasManyHomes?: boolean;
  showPayNow?: boolean;
  payNowDisabled?: boolean;
  onPayNowClick?: () => void;
};

function hasPayableExpenses(
  monthBalances: MonthlyStatementDetailInfoModel,
): boolean {
  return (
    monthBalances.homesDetails?.some((home) => {
      const { expenses, all } = home.groupedBalancesPerCategory || {};
      const balancesToCheck =
        all?.groupedBalancesInfo || expenses?.groupedBalancesInfo || [];
      return balancesToCheck.some(
        (b) => b.canBePaid && b.category === 'Expenses',
      );
    }) ?? false
  );
}

export function UpcomingSection({
  balances,
  selectedHome,
  hasManyHomes = false,
  isResident = false,
  handleFinancingModal,
  handleShowFinanceSuccessModal,
  handleShowBalanceDetailsModal,
  address = '',
  showPayNow = false,
  payNowDisabled = false,
  onPayNowClick,
}: UpcomingSectionProps) {
  if (!balances?.length) {
    return null;
  }

  return (
    <>
      <AccountPageSection
        content={balances?.map((monthBalances, index) => {
          const { homesDetails, monthSummaryInfo } = monthBalances;
          // `homesDetails` is optional on the model. Defaulting to `[]` keeps
          // every branch below identical when it is present, and renders nothing
          // instead of throwing when it is not.
          const homes =
            (selectedHome === 'all'
              ? homesDetails
              : homesDetails?.filter((home) => home.homeId === selectedHome)) ??
            [];

          return (
            <div className="mb-3xl last:mb-0" key={index}>
              <div className="mb-sm flex items-center justify-between border-0 border-b border-dashed border-gray pb-sm">
                <Text variant="h3" fontWeight="semibold">
                  {monthSummaryInfo && getMonthName(monthSummaryInfo.month)}
                </Text>
              </div>
              {homes.map((homeDetail) => (
                <MonthSummary
                  address={address}
                  homeDetail={homeDetail}
                  key={homeDetail.homeId}
                  onlyHome={
                    homes.length === 1 &&
                    (!hasManyHomes || selectedHome !== 'all')
                  }
                  handleFinancingModal={handleFinancingModal}
                  handleShowFinanceSuccessModal={handleShowFinanceSuccessModal}
                  handleShowBalanceDetailsModal={handleShowBalanceDetailsModal}
                  noBorder
                  hideSplitIt={isResident}
                />
              ))}
              {homes.length > 1 && (
                <div className="flex justify-between border-0 border-t border-dashed border-gray pt-sm">
                  <Text
                    variant="h3"
                    fontWeight="semibold"
                  >{`${homesDetails?.length} ${EARNINGS_STRINGS['monthly_statement.homes_total']}`}</Text>
                  <Money
                    className="text-h3-sm font-semibold"
                    value={monthSummaryInfo?.amount}
                    format="DOLLARS_WITH_CENTS"
                  />
                </div>
              )}
              {isResident &&
                showPayNow &&
                hasPayableExpenses(monthBalances) && (
                  <div className="mt-xl w-full md:flex md:flex-row md:justify-end">
                    <Button
                      size={{ _: 'fluid', md: 'default' }}
                      onClick={onPayNowClick}
                      disabled={payNowDisabled}
                    >
                      Make Payments...
                    </Button>
                  </div>
                )}
            </div>
          );
        })}
      />
    </>
  );
}
